import BasePlugin, {type GenericPlugin} from './base-plugin';
import * as dom from './dom';
import Player from './player';
import {setupStateEventEmission} from './reactive/state-event-emitter';
import Renderer from './renderer';
import {
  createArrWaveformState,
  type ArrWaveformActions,
  type ArrWaveformState,
} from './state/arrwaveform-state';
import Timer from './timer';
export type ArrWaveformOptions = {
  container: HTMLElement | string;
  height?: number | 'auto';
  width?: number | string;
  waveColor?: string | string[] | CanvasGradient;
  progressColor?: string | string[] | CanvasGradient;
  cursorColor?: string;
  cursorWidth?: number;
  barWidth?: number;
  barGap?: number;
  barRadius?: number;
  barHeight?: number;
  barAlign?: 'top' | 'bottom';
  barMinHeight?: number;
  minPxPerSec?: number;
  fillParent?: boolean;
  url?: string;
  peaks?: Array<Float32Array | number[]>;
  duration?: number;
  media?: HTMLMediaElement;
  mediaControls?: boolean;
  autoplay?: boolean;
  interact?: boolean;
  dragToSeek?: boolean | {debounceTime: number};
  hideScrollbar?: boolean;
  audioRate?: number;
  autoScroll?: boolean;
  autoCenter?: boolean;
  sampleRate?: number;
  splitChannels?: Array<Partial<ArrWaveformOptions> & {overlay?: boolean}>;
  normalize?: boolean;
  maxPeak?: number;
  plugins?: GenericPlugin[];
  renderFunction?: (
    peaks: Array<Float32Array | number[]>,
    ctx: CanvasRenderingContext2D
  ) => void;
  fetchParams?: RequestInit;
  backend?: 'WebAudio' | 'MediaElement';
  cspNonce?: string;
  blobMimeType?: string;
  waveformImageUrl?: string;
};
const defaultOptions = {
  waveColor: '#999',
  progressColor: '#555',
  cursorWidth: 1,
  minPxPerSec: 0,
  fillParent: true,
  interact: true,
  dragToSeek: false,
  autoScroll: true,
  autoCenter: true,
  sampleRate: 8000,
};
export type ArrWaveformEvents = {
  init: [];
  load: [url: string];
  loading: [percent: number];
  decode: [duration: number];
  ready: [duration: number];
  redraw: [];
  redrawcomplete: [];
  play: [];
  pause: [];
  finish: [];
  timeupdate: [currentTime: number];
  audioprocess: [currentTime: number];
  seeking: [currentTime: number];
  interaction: [newTime: number];
  click: [relativeX: number, relativeY: number];
  dblclick: [relativeX: number, relativeY: number];
  drag: [relativeX: number];
  dragstart: [relativeX: number];
  dragend: [relativeX: number];
  scroll: [
    visibleStartTime: number,
    visibleEndTime: number,
    scrollLeft: number,
    scrollRight: number,
  ];
  zoom: [minPxPerSec: number];
  destroy: [];
  error: [error: Error];
  resize: [];
};
class ArrWaveform extends Player<ArrWaveformEvents> {
  public options: ArrWaveformOptions & typeof defaultOptions;
  private renderer: Renderer;
  private timer: Timer;
  private plugins: GenericPlugin[] = [];
  private decodedData: AudioBuffer | null = null;
  private stopAtPosition: number | null = null;
  protected subscriptions: Array<() => void> = [];
  protected mediaSubscriptions: Array<() => void> = [];
  protected abortController: AbortController | null = null;
  private arrwaveformState: ArrWaveformState;
  private arrwaveformActions: ArrWaveformActions;
  private reactiveCleanups: Array<() => void> = [];
  public static readonly BasePlugin = BasePlugin;
  public static readonly dom = dom;
  public static create(options: ArrWaveformOptions) {
    return new ArrWaveform(options);
  }
  public getState(): ArrWaveformState {
    return this.arrwaveformState;
  }
  public getRenderer(): Renderer {
    return this.renderer;
  }
  constructor(options: ArrWaveformOptions) {
    const media = options.media || undefined;
    super({
      media,
      mediaControls: options.mediaControls,
      autoplay: options.autoplay,
      playbackRate: options.audioRate,
    });
    this.options = Object.assign({}, defaultOptions, options);
    const {state, actions} = createArrWaveformState({
      isPlaying: this.isPlayingSignal,
      currentTime: this.currentTimeSignal,
      duration: this.durationSignal,
      volume: this.volumeSignal,
      playbackRate: this.playbackRateSignal,
      isSeeking: this.seekingSignal,
    });
    this.arrwaveformState = state;
    this.arrwaveformActions = actions;
    this.timer = new Timer();
    const audioElement = media ? undefined : this.getMediaElement();
    this.renderer = new Renderer(this.options, audioElement);
    this.initPlayerEvents();
    this.initRendererEvents();
    this.initTimerEvents();
    this.initReactiveState();
    this.initPlugins();
    const initialUrl = this.options.url || this.getSrc() || '';
    Promise.resolve().then(() => {
      this.emit('init');
      const {peaks, duration} = this.options;
      if (initialUrl || (peaks && duration)) {
        this.load(initialUrl, peaks, duration).catch((err) => {
          this.emit(
            'error',
            err instanceof Error ? err : new Error(String(err))
          );
        });
      }
    });
  }
  private updateProgress(currentTime = this.getCurrentTime()): number {
    this.renderer.renderProgress(
      currentTime / this.getDuration(),
      this.isPlaying()
    );
    return currentTime;
  }
  private initTimerEvents() {
    this.subscriptions.push(
      this.timer.on('tick', () => {
        if (!this.isSeeking()) {
          const currentTime = this.updateProgress();
          this.emit('timeupdate', currentTime);
          this.emit('audioprocess', currentTime);
          if (
            this.stopAtPosition != null &&
            this.isPlaying() &&
            currentTime >= this.stopAtPosition
          ) {
            this.pause();
          }
        }
      })
    );
  }
  private initReactiveState() {
    this.reactiveCleanups.push(
      setupStateEventEmission(this.arrwaveformState, {
        emit: this.emit.bind(this),
      })
    );
  }
  private initPlayerEvents() {
    if (this.isPlaying()) {
      this.emit('play');
      this.timer.start();
    }
    this.mediaSubscriptions.push(
      this.onMediaEvent('timeupdate', () => {
        const currentTime = this.updateProgress();
        this.emit('timeupdate', currentTime);
      }),
      this.onMediaEvent('play', () => {
        this.emit('play');
        this.timer.start();
      }),
      this.onMediaEvent('pause', () => {
        this.emit('pause');
        this.timer.stop();
        this.stopAtPosition = null;
      }),
      this.onMediaEvent('emptied', () => {
        this.timer.stop();
        this.stopAtPosition = null;
      }),
      this.onMediaEvent('ended', () => {
        this.emit('timeupdate', this.getDuration());
        this.emit('finish');
        this.stopAtPosition = null;
      }),
      this.onMediaEvent('seeking', () => {
        this.emit('seeking', this.getCurrentTime());
      }),
      this.onMediaEvent('error', () => {
        this.emit(
          'error',
          (this.getMediaElement().error ?? new Error('Media error')) as Error
        );
        this.stopAtPosition = null;
      })
    );
  }
  private initRendererEvents() {
    this.subscriptions.push(
      this.renderer.on('click', (relativeX, relativeY) => {
        if (this.options.interact) {
          this.seekTo(relativeX);
          this.emit('interaction', relativeX * this.getDuration());
          this.emit('click', relativeX, relativeY);
        }
      }),
      this.renderer.on('dblclick', (relativeX, relativeY) => {
        this.emit('dblclick', relativeX, relativeY);
      }),
      this.renderer.on('scroll', (startX, endX, scrollLeft, scrollRight) => {
        const duration = this.getDuration();
        this.emit(
          'scroll',
          startX * duration,
          endX * duration,
          scrollLeft,
          scrollRight
        );
      }),
      this.renderer.on('render', () => {
        this.emit('redraw');
      }),
      this.renderer.on('rendered', () => {
        this.emit('redrawcomplete');
      }),
      this.renderer.on('dragstart', (relativeX) => {
        this.emit('dragstart', relativeX);
      }),
      this.renderer.on('dragend', (relativeX) => {
        this.emit('dragend', relativeX);
      }),
      this.renderer.on('resize', () => {
        this.emit('resize');
      })
    );
    {
      let debounce: ReturnType<typeof setTimeout> | undefined;
      const unsubscribeDrag = this.renderer.on('drag', (relativeX) => {
        if (!this.options.interact) return;
        this.renderer.renderProgress(relativeX);
        clearTimeout(debounce);
        let debounceTime = 0;
        const dragToSeek = this.options.dragToSeek;
        if (this.isPlaying()) {
          debounceTime = 0;
        } else if (dragToSeek === true) {
          debounceTime = 200;
        } else if (dragToSeek && typeof dragToSeek === 'object') {
          debounceTime =
            (dragToSeek as {debounceTime: number}).debounceTime ?? 200;
        }
        debounce = setTimeout(() => {
          this.seekTo(relativeX);
        }, debounceTime);
        this.emit('interaction', relativeX * this.getDuration());
        this.emit('drag', relativeX);
      });
      this.subscriptions.push(() => {
        clearTimeout(debounce);
        unsubscribeDrag();
      });
    }
  }
  private initPlugins() {
    if (!this.options.plugins?.length) return;
    this.options.plugins.forEach((plugin) => {
      this.registerPlugin(plugin);
    });
  }
  private unsubscribePlayerEvents() {
    this.mediaSubscriptions.forEach((unsubscribe) => unsubscribe());
    this.mediaSubscriptions = [];
  }
  public setOptions(options: Partial<ArrWaveformOptions>) {
    this.options = Object.assign({}, this.options, options);
    this.renderer.setOptions(this.options);
    if (options.audioRate) {
      this.setPlaybackRate(options.audioRate);
    }
    if (options.mediaControls != null) {
      this.getMediaElement().controls = options.mediaControls;
    }
  }
  public registerPlugin<T extends GenericPlugin>(plugin: T): T {
    if (this.plugins.includes(plugin)) {
      return plugin;
    }
    plugin._init(this);
    this.plugins.push(plugin);
    const unsubscribe = plugin.once('destroy', () => {
      this.plugins = this.plugins.filter((p) => p !== plugin);
      this.subscriptions = this.subscriptions.filter(
        (fn) => fn !== unsubscribe
      );
    });
    this.subscriptions.push(unsubscribe);
    return plugin;
  }
  public unregisterPlugin(plugin: GenericPlugin): void {
    this.plugins = this.plugins.filter((p) => p !== plugin);
    plugin.destroy();
  }
  public getWrapper(): HTMLElement {
    return this.renderer.getWrapper();
  }
  public getWidth(): number {
    return this.renderer.getWidth();
  }
  public getScroll(): number {
    return this.renderer.getScroll();
  }
  public setScroll(pixels: number) {
    return this.renderer.setScroll(pixels);
  }
  public setScrollTime(time: number) {
    const percentage = time / this.getDuration();
    this.renderer.setScrollPercentage(percentage);
  }
  public getActivePlugins() {
    return this.plugins;
  }
  private async loadAudio(
    url: string,
    blob?: Blob,
    channelData?: ArrWaveformOptions['peaks'],
    duration?: number
  ) {
    this.emit('load', url);
    if (!this.options.media && this.isPlaying()) this.pause();
    this.decodedData = null;
    this.stopAtPosition = null;
    this.abortController?.abort();
    this.abortController = null;
    if (!blob && !channelData) {
      const fetchParams = this.options.fetchParams || {};
      if (window.AbortController && !fetchParams.signal) {
        this.abortController = new AbortController();
        fetchParams.signal = this.abortController.signal;
      }
    }
    this.setSrc(url, blob);
    await new Promise<number>((resolve) => {
      const staticDuration = duration || this.getDuration();
      if (staticDuration) {
        resolve(staticDuration);
      } else {
        this.mediaSubscriptions.push(
          this.onMediaEvent(
            'loadedmetadata',
            () => resolve(this.getDuration()),
            {once: true}
          )
        );
      }
    });
    if (this.decodedData) {
      this.emit('decode', this.getDuration());
    }
    this.renderer.render(this.getDuration());
    this.emit('ready', this.getDuration());
  }
  public async load(
    url: string,
    channelData?: ArrWaveformOptions['peaks'],
    duration?: number
  ) {
    try {
      return await this.loadAudio(url, undefined, channelData, duration);
    } catch (err) {
      this.emit('error', err as Error);
      throw err;
    }
  }
  public async loadBlob(
    blob: Blob,
    channelData?: ArrWaveformOptions['peaks'],
    duration?: number
  ) {
    try {
      return await this.loadAudio('', blob, channelData, duration);
    } catch (err) {
      this.emit('error', err as Error);
      throw err;
    }
  }
  public zoom(minPxPerSec: number) {
    this.renderer.zoom(minPxPerSec);
    this.emit('zoom', minPxPerSec);
  }
  public getDecodedData(): AudioBuffer | null {
    return this.decodedData;
  }
  public exportPeaks({
    channels = 2,
    maxLength = 8000,
    precision = 10_000,
  } = {}): Array<number[]> {
    if (!this.decodedData) {
      throw new Error('The audio has not been decoded yet');
    }
    const maxChannels = Math.min(channels, this.decodedData.numberOfChannels);
    const peaks = [];
    for (let i = 0; i < maxChannels; i++) {
      const channel = this.decodedData.getChannelData(i);
      const data = [];
      const sampleSize = channel.length / maxLength;
      for (let j = 0; j < maxLength; j++) {
        const sample = channel.slice(
          Math.floor(j * sampleSize),
          Math.ceil((j + 1) * sampleSize)
        );
        let max = 0;
        for (let x = 0; x < sample.length; x++) {
          // eslint-disable-next-line security/detect-object-injection
          const n = sample[x];
          if (Math.abs(n) > Math.abs(max)) max = n;
        }
        data.push(Math.round(max * precision) / precision);
      }
      peaks.push(data);
    }
    return peaks;
  }
  public getDuration(): number {
    let duration = super.getDuration() || 0;
    if ((duration === 0 || duration === Infinity) && this.decodedData) {
      duration = this.decodedData.duration;
    }
    return duration;
  }
  public toggleInteraction(isInteractive: boolean) {
    this.options.interact = isInteractive;
  }
  public setTime(time: number) {
    this.stopAtPosition = null;
    super.setTime(time);
    this.updateProgress(time);
    this.emit('timeupdate', time);
  }
  public seekTo(progress: number) {
    const time = this.getDuration() * progress;
    this.setTime(time);
  }
  public async play(start?: number, end?: number): Promise<void> {
    if (start != null) {
      this.setTime(start);
    }
    const playResult = await super.play();
    if (end != null) {
      this.stopAtPosition = end;
    }
    return playResult;
  }
  public async playPause(): Promise<void> {
    return this.isPlaying() ? this.pause() : this.play();
  }
  public stop() {
    this.pause();
    this.setTime(0);
  }
  public skip(seconds: number) {
    this.setTime(this.getCurrentTime() + seconds);
  }
  public empty() {
    this.load('', [[0]], 0.001);
  }
  public setMediaElement(element: HTMLMediaElement) {
    this.unsubscribePlayerEvents();
    super.setMediaElement(element);
    this.initPlayerEvents();
  }
  public destroy() {
    this.emit('destroy');
    this.abortController?.abort();
    this.plugins.forEach((plugin) => plugin.destroy());
    this.subscriptions.forEach((unsubscribe) => unsubscribe());
    this.unsubscribePlayerEvents();
    this.reactiveCleanups.forEach((cleanup) => cleanup());
    this.reactiveCleanups = [];
    this.timer.destroy();
    this.renderer.destroy();
    super.destroy();
  }
}
export type {Signal, WritableSignal} from './reactive/arr-waveform-store';
export type {
  ArrWaveformActions,
  ArrWaveformState,
} from './state/arrwaveform-state';
export default ArrWaveform;
